/**
 * This browser's Activity device — `docs/activity.md` §4.6 (Aaron, D6).
 *
 * The **only** thing Activity keeps in `localStorage`: a key made once, which
 * every Activity request sends as `X-Rowted-Device`, so what the page
 * remembers is the server's, per person per device. Its kind is decided once,
 * by the first page width (`deviceKindOf`), and kept beside it. Lives outside
 * `client/src/activity` because that folder may not touch `localStorage`
 * (its drift row).
 */
import { uuidv7 } from 'uuidv7'
import { deviceKindOf } from '@rowted/shared'

const KEY = 'rowted.activity.device'
const KIND = 'rowted.activity.device-kind'

let memory: { key: string; kind: 'computer' | 'tablet' | 'phone' } | null = null

/** The key and kind, made the first time they are asked for. */
export function activityDevice(): { key: string; kind: 'computer' | 'tablet' | 'phone' } {
  if (memory) return memory
  let key: string | null = null
  let kind: string | null = null
  try {
    key = window.localStorage.getItem(KEY)
    kind = window.localStorage.getItem(KIND)
  } catch { /* a private window: a key for this visit only */ }
  if (!key || !/^[0-9a-f-]{36}$/i.test(key)) key = uuidv7()
  if (kind !== 'computer' && kind !== 'tablet' && kind !== 'phone') {
    const touchFirst = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
    kind = deviceKindOf(window.innerWidth, touchFirst)
  }
  try {
    window.localStorage.setItem(KEY, key)
    window.localStorage.setItem(KIND, kind)
  } catch { /* see above */ }
  memory = { key, kind: kind as 'computer' | 'tablet' | 'phone' }
  return memory
}

/** The headers every Activity request carries. */
export function activityHeaders(): Record<string, string> {
  const device = activityDevice()
  return { 'X-Rowted-Device': device.key, 'X-Rowted-Device-Kind': device.kind }
}
